import type { TextRun } from '@/api/types/stowed-element'
import type { TextRunStyle } from '@/lib/text/types/text-run-style'
import { cssLineHeightToStoredLeading } from '@/lib/text/leading'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { expandTabsToSpaces, filterOutOfRangeChars } from '@/lib/text/paste-sanitize'
import type { ExternalPasteOptions } from '../types/external-paste-options'
import type { StyleAccum } from '../types/style-accum'

/**
 * First family in a CSS `font-family` stack that resolves to a permissible family id.
 *
 * Generic CSS keywords are skipped: they are the browser's fallback vocabulary, not real families,
 * and matching one would quietly pin the run to whatever local family happens to share the name.
 * Returns `undefined` when nothing in the stack is available, which leaves the run on the caller's
 * base (caret) style — the deliberate print-safe fallback, since a family absent from the account's
 * permissible list cannot be placed on a page at all.
 */
export const GENERIC_CSS_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
  'inherit',
  'initial',
  'unset',
])

export function resolveFontFamilyFromCssStack(
  cssFontFamily: string,
  getFamilyIdByCssName: (cssName: string) => number | undefined
): number | undefined {
  for (const raw of cssFontFamily.split(',')) {
    const name = raw.trim()

    if (!name || GENERIC_CSS_FAMILIES.has(name.toLowerCase())) continue

    const resolved = getFamilyIdByCssName(name)

    if (resolved != null) return resolved
  }

  return undefined
}

export function resolveRun(
  accum: StyleAccum,
  text: string,
  baseStyle: TextRunStyle,
  fonts: ParagraphBlockFontLookup | undefined,
  { forceFontFamilyId, forceFontId, forcePointSize, onUnresolvedFamily }: ExternalPasteOptions = {}
): TextRun {
  // (see `resolveFontFamilyFromCssStack` above for how the family is chosen)
  // Resolve fontFamilyId: the clipboard's own CSS font-family wins over the base style, and
  // `forceFontFamilyId` (the destination's family) then wins over both.
  let { fontFamilyId } = baseStyle

  // Resolved FIRST and unconditionally, even when the result is about to be overridden below.
  // Two reasons, and the second is the one that bites: resolving is also what DETECTS an
  // unavailable typeface, and `onUnresolvedFamily` is the only input to the WMPCM-9199
  // "these fonts aren't available" toast. Skipping the lookup when forcing — which the caller now
  // always does for shape text (WMPCM-7028 AC2) — left that toast permanently unreachable while
  // every type still checked and every test still passed.
  if (accum.fontFamilyCssName && fonts?.getFamilyIdByCssName) {
    const resolved = resolveFontFamilyFromCssStack(
      accum.fontFamilyCssName,
      fonts.getFamilyIdByCssName
    )

    if (resolved != null) fontFamilyId = resolved
    // Nothing in the stack is a permissible family, so this run silently keeps the caret's font.
    // Report it rather than leaving the user to notice (WMPCM-9199) — the fallback itself is
    // correct and deliberate (an unlicensed family cannot be placed on a page), but a silent one is
    // indistinguishable from the bug it used to be.
    else onUnresolvedFamily?.(accum.fontFamilyCssName)
  }

  if (forceFontFamilyId != null) {
    fontFamilyId = forceFontFamilyId
  }

  // Resolve fontId from the (possibly updated) fontFamilyId + bold/italic variant — this still
  // uses the clipboard's own bold/italic even when the family itself was forced, so e.g. bold
  // Times New Roman becomes bold in the destination family, not just its regular weight.
  // `forceFontId` (the destination's font style, WMPCM-13346) wins over that variant.
  let { fontId } = baseStyle

  if (forceFontId != null) {
    fontId = forceFontId
  } else if (fontFamilyId != null && fonts?.getFontIdByVariant) {
    const resolved = fonts.getFontIdByVariant(fontFamilyId, accum.bold, accum.italic)

    if (resolved != null) fontId = resolved
  }

  const tracking = accum.letterSpacingEm !== undefined ? accum.letterSpacingEm : baseStyle.tracking

  // Hoisted above the return because the leading converter needs the run's FINAL point size: a
  // ratio-form `line-height` (`150%`, `1.3`, `2em`) only resolves to a pt length against it — and
  // `forcePointSize` (WMPCM-7028 AC2) is part of "final", so a forced destination size is what an
  // out-of-band ratio resolves against, not the size the clipboard asked for.
  const pointSize = forcePointSize ?? accum.pointSize ?? baseStyle.pointSize

  return {
    ...baseStyle,
    fontFamilyId,
    fontId,
    tracking,
    kerning: accum.letterSpacingEm !== undefined ? undefined : baseStyle.kerning,
    // Converted, never stored raw: the clipboard's `line-height` is 96dpi CSS, and
    // `storedLeadingNumber` would read `41.6px` as 41.6pt and `150%` as 150pt. Falls back to the
    // destination's own leading whenever the source specifies none (`normal`, absent, unparseable).
    leading: cssLineHeightToStoredLeading(accum.lineHeight, pointSize) ?? baseStyle.leading,
    underline: accum.underline || undefined,
    strikethrough: accum.strikethrough || undefined,
    // `forcePointSize` (destination size) wins over the clipboard's own font-size, mirroring how
    // `forceFontFamilyId` wins over its font-family — the two are the "font style and size come
    // from the destination" pair (WMPCM-7028 AC2). Underline/strikethrough are NOT normalised:
    // they are the "retains its format" half of the same AC. Bold/italic are the font style,
    // which `forceFontId` sets when the caller passes it.
    pointSize,
    // WMPCM-10141 — same "retains its format" half as underline/strikethrough above:
    // `verticalAlign` is not touched by `forceFontFamilyId`/`forcePointSize`. The *effective*
    // (scaled-down) size a superscript/subscript run measures and paints at is derived from this
    // LOGICAL `pointSize` downstream (`effectivePointSizeForRun`, text-run-vertical-align.ts) —
    // scaling composes with forcing, it does not replace it.
    verticalAlign: accum.verticalAlign,
    // WMPCM-10141: expand tabs to spaces before the range filter — a real tab (U+0009) is well
    // within range and would otherwise pass through untouched, then silently collapse to a
    // single space's width when measured/rendered (no tab-stop support — see
    // `expandTabsToSpaces`'s doc comment).
    textContent: filterOutOfRangeChars(expandTabsToSpaces(text)),
  }
}
